import React from "react";

import cx from "classnames";
import omit from "lodash.omit";

import { useUIDSeed } from "../../helpers/useUIDSeed";
import baseInputStyles from "../Input/Input.scss";

import multiSelectStyles from "./MultiSelect.scss";
import { MultiSelectItem } from "./MultiSelectItem";

interface MultiSelectInputProps<T extends string> {
  inputRef: React.Ref<HTMLInputElement>;
  containerRef?: React.Ref<HTMLDivElement>;
  inputProps: {
    error?: boolean | string;
    disabled?: boolean;
    placeholder?: string;
    [key: string]: unknown;
  };
  maxSelections?: number;
  selectedItems: MultiSelectItem<T>[];
  renderSelection: (props: {
    tabIndex: number;
    item: MultiSelectItem<T>;
    onRemoveSelection: (item: MultiSelectItem<T>) => void;
  }) => React.ReactNode;
  onRemoveSelection: (item: MultiSelectItem<T>) => void;
  suggestionsOpen?: boolean;
  hideSearchInput?: boolean;
  alwaysDisplayInput?: boolean;
  ariaDescribedBy?: string;
  listId: string;
  highlightedIndex: string | null;
}

export default function MultiSelectInput<T extends string>({
  inputRef,
  containerRef,
  inputProps: { error, disabled, placeholder, ...inputProps },
  maxSelections = 0,
  selectedItems,
  renderSelection,
  onRemoveSelection,
  suggestionsOpen,
  hideSearchInput,
  alwaysDisplayInput,
  ariaDescribedBy,
  listId,
  highlightedIndex,
}: MultiSelectInputProps<T>) {
  const generateUID = useUIDSeed(listId);
  const hasHighlightedIndex = highlightedIndex !== null;
  const itemId = hasHighlightedIndex ? generateUID(highlightedIndex) : null;
  const activeDescendantProps = {
    ...(hasHighlightedIndex && itemId
      ? { "aria-activedescendant": itemId }
      : null),
  };
  // hide the input on mouse off if there is 1 selected item already
  const input =
    suggestionsOpen || !selectedItems.length
      ? "input-wrapper"
      : "input-hide-wrapper";

  // overwrite the class if the alwaysDisplayInput prop is ON
  const multiSelectInputStyle = alwaysDisplayInput ? "input-wrapper" : input;

  const wrapperClasses = cx(
    multiSelectStyles[multiSelectInputStyle],
    baseInputStyles["input"],
    {
      [baseInputStyles["input--disabled"]]: disabled,
      [baseInputStyles["input--error"]]: error,
      [multiSelectStyles["input--hide"]]: hideSearchInput,
      [multiSelectStyles["input--active"]]: suggestionsOpen,
    }
  );
  const disallowMoreSelections =
    maxSelections !== 0 && selectedItems.length >= maxSelections;

  const inputStyle = disallowMoreSelections ? { display: "none" } : {};
  return (
    <div className={wrapperClasses} data-testid="multiselect-input-test">
      <div
        ref={containerRef}
        className={multiSelectStyles["selection-container"]}
      >
        {selectedItems.map((item, i) => (
          <div
            key={generateItemKey("dropdown", item.id)}
            className={multiSelectStyles["selection-items-container"]}
          >
            {renderSelection({ tabIndex: i, item, onRemoveSelection })}
          </div>
        ))}
        <div className={multiSelectStyles["selection-input-container"]}>
          <input
            style={inputStyle}
            ref={inputRef}
            disabled={disabled}
            className={multiSelectStyles["input"]}
            placeholder={placeholder}
            {...omit(inputProps, "onTextChange")}
            aria-describedby={ariaDescribedBy}
            role="combobox"
            aria-autocomplete="both"
            aria-controls={listId}
            aria-expanded={suggestionsOpen}
            {...activeDescendantProps}
          />
        </div>
      </div>
    </div>
  );
}
function generateItemKey(comp: string, id: string): string {
  return comp + "-" + id.replace(/ /g, "-");
}
